import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import { supabase } from '../../supabaseClient';
import { documentDelegationEmail, sendEmail } from '../../lib/email';
import { formatDateBritish } from '../../lib/compliance';
import {
  scopesFor, createDelegation, revokeDelegation, reissueDelegation,
  listDelegations, delegationState, isOpen, DELEGATION_DAYS,
} from '../../lib/delegation';

// "Ask someone else to do this" – a supplier handing ONE document to a
// colleague better placed to complete or sign it.
//
// The delegate gets no account and no portal access: a link, one document, and
// an expiry. See api/delegation-lookup.js for why they don't get a session.

const TONE = {
  green: 'bg-[#92D400]/10 text-fusion-forest border-[#92D400]/40',
  amber: 'bg-[#E89A2E]/15 text-[#7A4E10] border-[#E89A2E]/40',
  grey: 'bg-[#EFF1E7] text-[#51534A] border-line',
};

// expires_at is a timestamptz; formatDateBritish parses a DATE-ONLY string and
// returns null for anything else – which is why the modal read "expires null".
const expiryLabel = (ts) => (ts ? formatDateBritish(String(ts).slice(0, 10)) : null);

export default function DelegateDocumentModal({ document: docMeta, supplierId, supplierName, invitedByUserId, invitedByName, onClose, onChanged }) {
  const scopes = useMemo(() => scopesFor(docMeta?.satisfiedBy), [docMeta]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [scope, setScope] = useState(scopes[0].key);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [existing, setExisting] = useState([]);
  const [lastLink, setLastLink] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const all = await listDelegations(supplierId);
      setExisting(all.filter(d => d.document_key === docMeta.key));
    } catch (e) { setError(`Couldn't load who you've already asked – ${e.message}`); }
  }, [supplierId, docMeta.key]);
  useEffect(() => { load(); }, [load]);

  const openOnes = existing.filter(isOpen);

  const send = async () => {
    setError(null); setNotice(null);
    if (!email.trim()) { setError('An email address is needed – that\'s where the link goes.'); return; }
    setBusy(true);
    try {
      const { delegation, url } = await createDelegation({
        supplierId, documentKey: docMeta.key, invitedByUserId, name, email, role, scope,
      });

      // The row exists now. If the EMAIL fails the delegation is still valid,
      // so we surface the link rather than pretending nothing happened – the
      // supplier can paste it into their own email and the work isn't lost.
      const payload = documentDelegationEmail({
        inviteeName: name,
        // Naming the colleague who asked is the whole reason this email gets
        // opened – the recipient has no relationship with Fusion 2K.
        inviterName: invitedByName,
        supplierName,
        documentLabel: docMeta.label,
        scope,
        expiresOn: expiryLabel(delegation.expires_at),
        linkUrl: url,
      });
      let emailed = true;
      try {
        await sendEmail({ to: email.trim(), subject: payload.subject, html: payload.html });
      } catch { emailed = false; }

      // Best-effort log; a failure here must not imply the invite failed.
      supabase.from('supplier_email_log').insert([{
        supplier_id: supplierId,
        email_kind: 'document_delegation',
        recipients: [email.trim()],
        sent_by: invitedByUserId || null,
      }]).then(({ error: logErr }) => { if (logErr) console.warn('delegation email not logged:', logErr.message); });

      setLastLink(url);
      setNotice(emailed
        ? `Sent to ${email.trim()}. The link works for ${DELEGATION_DAYS} days.`
        : `The request was created, but the email didn't send. Copy the link below and send it to ${email.trim()} yourself.`);
      setName(''); setEmail(''); setRole('');
      load();
      onChanged?.();
    } catch (e) {
      setError(`Couldn't create the request – ${e.message}`);
    }
    setBusy(false);
  };

  const withdraw = async (id) => {
    setError(null); setNotice(null);
    try { await revokeDelegation(id); setNotice('Withdrawn – that link no longer works.'); load(); onChanged?.(); }
    catch (e) { setError(`Couldn't withdraw it – ${e.message}`); }
  };

  const reissue = async (id) => {
    setError(null); setNotice(null);
    try {
      const { url } = await reissueDelegation(id);
      setLastLink(url);
      setNotice(`New link created – the old one no longer works. Send this one instead.`);
      load(); onChanged?.();
    } catch (e) { setError(`Couldn't create a new link – ${e.message}`); }
  };

  return (
    <Modal open onClose={busy ? undefined : onClose} maxWidth={560} stripe={false} ariaLabel={`Send ${docMeta.label} to someone else to complete`}>
      <Modal.Header eyebrow="Send to someone else" title={docMeta.label} sub="Send this to a colleague who's better placed to deal with it." onClose={busy ? undefined : onClose} />
      <Modal.Body>
        {notice && <p role="status" className="font-['Metropolis'] text-[13px] text-fusion-forest mb-3">{notice}</p>}
        {error && <p role="alert" className="font-['Metropolis'] text-[13px] text-[#B01F06] mb-3">{error}</p>}

        {/* Shown whenever a link has just been made – including when the email
            failed, which is exactly when it matters most. */}
        {lastLink && (
          <div className="mb-4 rounded-lg border border-line bg-[#FAFBF4] px-3 py-2.5">
            <p className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1">Their link</p>
            <p className="font-mono text-[11px] text-ink break-all">{lastLink}</p>
          </div>
        )}

        {/* ONE AT A TIME. Two people completing the same document is two
            signatures on one obligation, and whichever lands second silently
            overwrites the first – with no way afterwards to say who actually
            agreed to it. So while a request is outstanding the form is replaced
            by the request itself: withdraw it to ask someone else. */}
        {openOnes.length > 0 ? (
          <div className="rounded-xl border border-[#EFD9A9] bg-[#FBF0DC] px-4 py-3.5">
            <p className="font-['Metropolis'] text-[13px] font-semibold text-[#5E3D05]">
              Already with {openOnes[0].invitee_name || openOnes[0].invitee_email}
            </p>
            <p className="font-['Metropolis'] text-[12px] text-[#5E3D05] mt-1 leading-relaxed">
              {openOnes[0].scope === 'complete_and_sign'
                ? 'They can complete and sign it.'
                : 'They can fill it in, then it comes back to you to sign.'}
              {expiryLabel(openOnes[0].expires_at) ? ` Their link works until ${expiryLabel(openOnes[0].expires_at)}.` : ''}
            </p>
            <p className="font-['Metropolis'] text-[12px] text-[#5E3D05] mt-2 leading-relaxed">
              Only one person can be asked at a time. To send it to someone else,
              withdraw this request first.
            </p>
            <button type="button" disabled={busy} onClick={() => withdraw(openOnes[0].id)}
              className="mt-2.5 rounded-lg bg-white border border-[#EAD5A4] text-[#7A4E10] font-['Metropolis'] text-[12px] font-bold px-3.5 py-1.5 hover:border-[#B47509] transition-colors disabled:opacity-50">
              Withdraw the request
            </button>
          </div>
        ) : (
        <div className="flex flex-col gap-3">
          <div>
            <label htmlFor="del-name" className="block font-['Metropolis'] text-[12px] font-semibold text-[#51534A] mb-1">Their name</label>
            <input id="del-name" value={name} onChange={e => setName(e.target.value)} disabled={busy}
              className="w-full bg-white border border-line rounded-lg px-3 py-2 font-['Metropolis'] text-sm text-ink focus:outline-none focus:border-[#92D400]" />
          </div>
          <div>
            <label htmlFor="del-email" className="block font-['Metropolis'] text-[12px] font-semibold text-[#51534A] mb-1">
              Their email <span className="text-[#E0290A]">*</span>
            </label>
            <input id="del-email" type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={busy}
              className="w-full bg-white border border-line rounded-lg px-3 py-2 font-['Metropolis'] text-sm text-ink focus:outline-none focus:border-[#92D400]" />
          </div>
          <div>
            <label htmlFor="del-role" className="block font-['Metropolis'] text-[12px] font-semibold text-[#51534A] mb-1">Their role <span className="font-normal text-[#9BA08C]">(optional)</span></label>
            <input id="del-role" value={role} onChange={e => setRole(e.target.value)} disabled={busy} placeholder="e.g. Managing Director"
              className="w-full bg-white border border-line rounded-lg px-3 py-2 font-['Metropolis'] text-sm text-ink focus:outline-none focus:border-[#92D400]" />
          </div>

          <fieldset>
            <legend className="font-['Metropolis'] text-[12px] font-semibold text-[#51534A] mb-1.5">What should they do?</legend>
            <div className="flex flex-col gap-2">
              {scopes.map(s => (
                <label key={s.key} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${scope === s.key ? 'border-[#92D400] bg-[#92D400]/[0.06]' : 'border-line hover:border-[#DADCCD]'}`}>
                  <input type="radio" name="del-scope" checked={scope === s.key} onChange={() => setScope(s.key)} disabled={busy}
                    className="mt-0.5 w-4 h-4 accent-active-green flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="block font-['Metropolis'] text-[13px] font-semibold text-ink">{s.label}</span>
                    <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">{s.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-relaxed">
            They don't need an account. The link is for this document only, expires after {DELEGATION_DAYS} days,
            and you can withdraw it at any time. They can't pass it on to anyone else, and only one person can
            be asked at a time.
          </p>
        </div>
        )}

        {/* History – withdrawn, expired and completed requests. The OUTSTANDING
            one is shown above instead, so it isn't listed twice. */}
        {existing.some(d => !isOpen(d)) && (
          <div className="mt-5 pt-4 border-t border-line">
            <p className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-2">
              Previously asked
            </p>
            <ul className="flex flex-col gap-2">
              {existing.filter(d => !isOpen(d)).map(d => {
                const st = delegationState(d);
                return (
                  <li key={d.id} className="flex items-start justify-between gap-3 rounded-lg border border-line px-3 py-2">
                    <div className="min-w-0">
                      <p className="font-['Metropolis'] text-[13px] font-semibold text-ink truncate">{d.invitee_name || d.invitee_email}</p>
                      <p className="font-['Metropolis'] text-[11px] text-[#51534A]">
                        {d.scope === 'complete_and_sign' ? 'To complete and sign' : 'To fill in, then back to you'}
                        {d.expires_at ? ` · expires ${expiryLabel(d.expires_at)}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.12em] rounded-md px-2 py-0.5 border whitespace-nowrap ${TONE[st.tone]}`}>
                        {st.label}
                      </span>
                      {st.key === 'expired' && (
                        <button type="button" onClick={() => reissue(d.id)}
                          className="font-['Metropolis'] text-[11px] font-semibold text-fusion-forest hover:underline">New link</button>
                      )}
                      {isOpen(d) && (
                        <button type="button" onClick={() => withdraw(d.id)}
                          className="font-['Metropolis'] text-[11px] font-semibold text-[#B01F06] hover:underline">Withdraw</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} disabled={busy}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50">
            Close
          </button>
          <button type="button" onClick={send} disabled={busy || !email.trim()}
            className="rounded-xl bg-[#92D400] text-[#131313] font-['Metropolis'] text-sm font-bold px-5 py-2 transition-colors hover:bg-[#a4e810] disabled:opacity-45 disabled:cursor-not-allowed">
            {busy ? 'Sending…' : 'Send the request'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
